/***
*
*   DASHBOARD TABLE HELPERS
*   Pure logic behind the dashboard verifications table: the localStorage key
*   its view state is scoped to, and one row of its CSV export.
*
*   Kept out of dashboard.js so it can be unit tested — importing the view pulls
*   in components/lib, whose barrel cannot be loaded under jest. Same reason
*   screening-summary.js and pdf-report.js sit beside their views rather than
*   inside them.
*
**********/

import jwt_decode from "jwt-decode";
import {
  derivePepResult,
  deriveSanctionsResult,
  deriveOtherScreeningResult,
  deriveAdverseMediaResult,
} from "../verification/screening-summary";

const SHARED_TABLE_STATE_KEY = "dashboardVerificationsTable";

/**
 * One storage key per account + user. localStorage is per browser; signout
 * clears it but switchAccount does not, so a single shared key would carry one
 * account's filters — which can hold a customer's name — into another account's
 * view of the same table.
 *
 * Reads the cached token directly rather than the /api/user response, because
 * the state is restored during the table's first render, well before that
 * request resolves.
 *
 * @param {object} cachedUser - the parsed localStorage "user" object, or null
 * @returns {string} an account+user scoped key, or the shared fallback
 */
export function tableStateKey(cachedUser) {
  try {
    const { accountId, userId } = jwt_decode(cachedUser?.token);
    if (accountId && userId)
      return `${SHARED_TABLE_STATE_KEY}:${accountId}:${userId}`;
  } catch (err) {
    // No token yet, or an unparseable one.
  }

  return SHARED_TABLE_STATE_KEY;
}

/**
 * One row of the CSV export. All four screening fields run through the same
 * helpers as the matching table columns, so the export and the dashboard can
 * never disagree about a row.
 *
 * These keys are also the CSV header row (the export sets useKeysAsHeaders),
 * which is why there is no second list of header strings to keep in step —
 * export-to-csv treats a columnHeaders entry as a lookup key, so a drifting
 * list silently emitted an empty column.
 */
export function buildExportRow(v) {
  return {
    id: v.id,
    first_name: v.first_name,
    last_name: v.last_name,
    type: v.type,
    result: v.result,
    created_at: v.created_at,
    email: v.email,
    updated_at: v.updated_at,
    pep_results: derivePepResult(v),
    sanctions_results: deriveSanctionsResult(v),
    other_screening_results: deriveOtherScreeningResult(v),
    adverse_media_results: deriveAdverseMediaResult(v),
  };
}
